import IconInformative from "components/EvtKit/icons/IconInformative"
import Input from "components/EvtKit/input/Input"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import SwitchV2 from "components/EvtKit/switch/SwitchV2"
import Tooltip from "components/Tooltip"
import { authMethodSelectOptions } from "constants/product"

import { memo, useCallback, useEffect, useState } from "react"
import styled from "styled-components"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
`
const MiniContainers = styled.div`
  display: flex;
  gap: 48px;
  justify-content: space-between;
  margin-bottom: 0px;
`

const AuthFieldsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
`

const SelectContainer = styled.div`
  width: 100%;
`

const InputContainer = styled.div`
  width: 100%;
  height: 48px;
`
const IconContainerWithBottomTooltip = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: -5px;
  cursor: pointer;
  & span {
    top: 24px;
    width: 268px;
    text-align: left;
  }
`
const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  width: 860px;
`

const CheckboxContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const RefreshIcon = styled.div`
  position: absolute;
  padding-left: 208px;
  transform: translateY(-50%);
  z-index: 2;
  font-size: 24px;
  color: ${(props) => props.theme.palette.subtitle};
  cursor: pointer;
  user-select: none;
`

interface Props {
  externalFields: App.ExternalProductInfo
  setExternalFields: React.Dispatch<React.SetStateAction<App.ExternalProductInfo>>
  isExternal: boolean
  setIsExternal: React.Dispatch<React.SetStateAction<boolean>>
}

const IsExternal = (props: Props) => {
  const { externalFields, setExternalFields, isExternal, setIsExternal } = props

  const [authMethod, setAuthMethod] = useState("")

  useEffect(() => {
    if (externalFields.authMethod) {
      setAuthMethod(externalFields.authMethod)
    }
  }, [externalFields.authMethod])

  const handleExternalFields = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setExternalFields((prevValue) => ({ ...prevValue, [e.target.name]: e.target.value }))

      if (authMethod) {
        setExternalFields((prevValue) => ({ ...prevValue, authMethod }))
      }
    },
    [authMethod, setExternalFields]
  )
  const handleCheckboxChangeExternal = useCallback(() => {
    setIsExternal((prevIsExternal: boolean) => !prevIsExternal)
  }, [setIsExternal])

  const handleSelectChange = useCallback(
    (option?: Option | null) => {
      const optionValue = option?.value

      if (optionValue) {
        setAuthMethod(optionValue)
      }
    },
    [setIsExternal]
  )

  return (
    <Container>
      <CheckboxContainer>
        <CheckboxContent>
          <SwitchV2 label="Produto Externo" value={isExternal} onChange={handleCheckboxChangeExternal} />
          <RefreshIcon id="external">
            <IconContainerWithBottomTooltip>
              <Tooltip text="Produto/serviço em formato de API, que expõe endpoints e não possui interface frontend, somente via API REST. Aqui o Shelf cuidará da cobrança, gestão de acesso e chaves API e interagirá com a sua API a partir do protocol de autenticação configurado abaixo.">
                <IconInformative />
              </Tooltip>
            </IconContainerWithBottomTooltip>
          </RefreshIcon>
        </CheckboxContent>
      </CheckboxContainer>

      <MiniContainers>
        <InputContainer>
          <Input
            id="externalApiUrl"
            type="text"
            name="externalApiUrl"
            label="URL Api do Produto Externo *"
            className="fullWidth search-input"
            value={externalFields.externalApiUrl}
            onChange={handleExternalFields}
            required={isExternal}
            safeBottom
            disabled={!isExternal}
            placeholder="Digite"
            shrink
          />
        </InputContainer>
      </MiniContainers>

      <MiniContainers>
        <AuthFieldsContainer>
          <SelectContainer>
            <SelectV2
              id="authMethod"
              options={authMethodSelectOptions}
              value={authMethod}
              label="Método de autenticação *"
              onChange={handleSelectChange}
              disabled={!isExternal}
              className="fullWidth"
            />
          </SelectContainer>
          <InputContainer>
            <Input
              id="authKey"
              type="text"
              name="authKey"
              label="Chave de autenticação *"
              className="fullWidth search-input"
              value={externalFields.authKey}
              onChange={handleExternalFields}
              required={false}
              safeBottom
              disabled={!isExternal}
              placeholder="Digite"
              shrink
            />
          </InputContainer>
        </AuthFieldsContainer>
      </MiniContainers>

      <MiniContainers>
        <InputContainer>
          <Input
            id="docTitle"
            type="text"
            name="docTitle"
            label="Título documentação *"
            className="fullWidth search-input"
            value={externalFields.docTitle}
            onChange={handleExternalFields}
            required={false}
            safeBottom
            disabled={!isExternal}
            placeholder="Digite"
            shrink
          />
        </InputContainer>
      </MiniContainers>

      <MiniContainers>
        <InputContainer>
          <Input
            id="docDescription"
            type="text"
            name="docDescription"
            label="Descrição documentação *"
            className="fullWidth search-input"
            value={externalFields.docDescription ?? ""}
            onChange={handleExternalFields}
            required={false}
            disabled={!isExternal}
            placeholder="Digite"
            shrink
          />
        </InputContainer>
      </MiniContainers>
    </Container>
  )
}

export default memo(IsExternal)
